// src/hooks/useChat.js
import { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { STORAGE_KEYS } from "../core/config";
import { sendChatMessage, sendChatDocument, clearChatSession } from "../services/chatService";
import { getBrdAutoAdvanceAction } from "../utils/brdEstimationChain";

function estimationFieldsFromResponse({
  estimationValue,
  featureOptions,
  estimationOptions,
  qaHours,
  moduleEstimatedHours,
  estimationSummary,
  totalHoursDict,
  phases,
}) {
  return {
    estimation_value: estimationValue || null,
    feature_options: featureOptions ?? null,
    estimation_options: Array.isArray(estimationOptions) ? estimationOptions : null,
    qa_hours: qaHours ?? null,
    module_estimated_hours: moduleEstimatedHours ?? null,
    estimation_summary: estimationSummary ?? null,
    total_hours_dict: totalHoursDict ?? null,
    phases: Array.isArray(phases) ? phases : null,
  };
}

export function useChat() {
  const [sessionId, setSessionId] = useState("");
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [slot, setSlot] = useState(null);
  /** Incremented on New Chat / session reset so in-flight /chat responses are ignored. */
  const clearGenerationRef = useRef(0);

  // ---------------------------
  // Initialize / load session id
  // ---------------------------
  useEffect(() => {
    try {
      let stored = window.localStorage.getItem(STORAGE_KEYS.SESSION_ID);
      if (!stored) {
        stored = uuidv4();
        window.localStorage.setItem(STORAGE_KEYS.SESSION_ID, stored);
      }
      setSessionId(stored);
    } catch {
      // localStorage fallback
      setSessionId(uuidv4());
    }
  }, []);

  const injectSystemMessage = useCallback((text) => {
    setMessages(prev => {
      if (prev.length > 0) return prev; // prevent duplicates

      return [
        {
          id: `assistant-system-${Date.now()}`,
          role: "assistant",
          content: text,
          createdAt: new Date(),
        }
      ];
    });
  }, []);

  // ---------------------------
  // sendMessage (UPDATED)
  // ---------------------------
  const sendMessage = useCallback(
    async (text, options = null) => {
      /**
       * ✅ NEW: Allow frontend-only assistant injection
       * Used for first welcome message
       */
      if (
        options?.silent === true &&
        options?.role === "assistant"
      ) {
        setMessages(prev => [
          ...prev,
          {
            id: `assistant-init-${Date.now()}`,
            role: "assistant",
            content: text,
            createdAt: new Date(),
          }
        ]);
        return undefined;
      }

      const trimmed = (text || "").trim();
      if (!trimmed) return undefined;
      if (isLoading && !options?.brdEstimationChain) return undefined;

      const explicitSlot = options?.explicitSlot ?? null;
      const slotToUse = explicitSlot !== null ? explicitSlot : slot;

      // Session resets are only ever done explicitly via clearChat() (the "New Chat" action),
      // which coordinates with the backend (/clear_chat) to flush/delete the old Redis session.
      // Do NOT infer "new project" from message text here: earlier heuristics (regex-matching
      // phrases like "build ... app" plus a crude "Total"/"estimated"/"hours" content check on
      // the last assistant reply) false-positived on ordinary follow-ups about an *existing*
      // estimation — those words are exactly this product's domain vocabulary — silently
      // minting a fresh session_id and wiping local messages/slot without telling the backend.
      // The backend then saw a pristine session_dict and had no memory of the BRD/estimation
      // just generated, which looked like "the bot forgot everything" on the next message.
      const currentSessionId = sessionId;

      if (!currentSessionId) return undefined;

      const now = new Date();
      
      // Check if this is a silent update (for module add/delete or role-platform assignment)
      const isSilentUpdate = options?.silent === true && options?.updateExisting === true;
      
      // Also check if this is a role-platform assignment (should be silent)
      const isRolePlatformAssignment = options?.silent === true && 
                                      (slotToUse === 'role_platform_assignments' || 
                                       trimmed.startsWith('{') && trimmed.includes('"roles"') && trimmed.includes('"platform"'));

      // Only add user message if not a silent update (BRD auto-chain uses suppressUserMessage)
      if (!isSilentUpdate && !isRolePlatformAssignment && !options?.suppressUserMessage) {
        const displayContent =
          options?.documentUploadLabel?.trim() || trimmed;
        const userMessage = {
          id: `user-${now.getTime()}`,
          role: "user",
          content: displayContent,
          createdAt: now,
        };

        setMessages(prev => [...prev, userMessage]);
      }
      
      setIsLoading(true);
      const genAtStart = clearGenerationRef.current;

      try {
        const updatedProjectScope = options?.updatedProjectScope ?? null;
        const docUserInput = (options?.documentUserInput ?? "").trim();
        const useMultipartDoc =
          options?.documentFile instanceof File &&
          typeof File !== "undefined";

        const {
          aiReply,
          slot: responseSlot,
          options: responseOptions,
          preselectedOptions: responsePreselectedOptions,
          selection_type: responseSelectionType,
          projectScope: responseProjectScope,
          estimationValue: responseEstimationValue,
          featureOptions: responseFeatureOptions,
          estimationOptions: responseEstimationOptions,
          qaHours: responseQaHours,
          moduleEstimatedHours: responseModuleEstimatedHours,
          estimationSummary: responseEstimationSummary,
          totalHoursDict: responseTotalHoursDict,
          phases: responsePhases,
        } = useMultipartDoc
          ? await sendChatDocument(currentSessionId, options.documentFile, {
              userInput: docUserInput,
              slot: slotToUse,
              signal: options?.abortSignal,
            })
          : await sendChatMessage(
              currentSessionId,
              trimmed,
              slotToUse,
              updatedProjectScope,
              options?.documentText ?? null,
              { signal: options?.abortSignal }
            );

        if (clearGenerationRef.current !== genAtStart) {
          setIsLoading(false);
          return undefined;
        }

        if (responseSlot !== null && responseSlot !== undefined) {
          setSlot(responseSlot);
        }

        const brdChainDepth = options?.brdChainDepth ?? 0;
        const isBrdPipeline =
          Boolean(
            options?.documentFile ||
              options?.documentText ||
              options?.brdEstimationChain
          ) && brdChainDepth < 12;

        if (isBrdPipeline) {
          const advance = getBrdAutoAdvanceAction(
            {
              aiReply,
              responseSlot,
              projectScope: responseProjectScope,
              estimationValue: responseEstimationValue,
            },
            brdChainDepth
          );
          if (advance) {
            return await sendMessage("Continue", {
              explicitSlot: responseSlot,
              suppressUserMessage: true,
              brdEstimationChain: true,
              brdChainDepth: brdChainDepth + 1,
              abortSignal: options?.abortSignal,
              uploadCancelRef: options?.uploadCancelRef,
            });
          }
        }

        // Check if this is a roles update (before modules) - if so, update existing message instead of adding new one
        // Prioritize updateExisting flag: if explicitly set and slot matches, force update
        const isRolesUpdate = (options?.updateExisting === true && responseSlot === 'roles.items') ||
                             aiReply.includes('👥 **Project Roles**') || 
                             (responseSlot === 'roles.items' && aiReply.includes('•'));
        
        // Check if this is a role-platform modules response (after assignment, grouped by platform)
        // Backend sends "👥 **Role Platform Assignments**" and "📦 **Key Modules for {Platform}**"
        const isRolePlatformModulesResponse = aiReply.includes('Key Modules for') ||
                                            aiReply.includes('Role Platform Assignments') ||
                                            (aiReply.includes('Roles:') && 
                                             (aiReply.includes('Key Modules for Mobile') || aiReply.includes('Key Modules for Web')));
        
        // Check if this is a role modules update - if so, update existing message instead of adding new one
        // Prioritize updateExisting flag: if explicitly set and slot matches OR response contains role modules pattern, force update
        // IMPORTANT: Don't treat confirmation summary as a module update - if backend returns wrong response, don't update
        const isConfirmationSummary = aiReply.includes('📋 **Please review your selections:**') ||
                                     aiReply.includes('Would you like to proceed with estimation?');
        // features.review (the read-only feature list shown after "Estimate") reuses the
        // same "### 👤 **" markup as the modules screen — without this guard the two
        // unconditional content-only checks below classify it as a modules update and
        // silently overwrite the (now off-screen) modules bubble instead of appending a
        // new message, which looks like tapping Estimate "does nothing".
        const isFeaturesReviewResponse = responseSlot === 'features.review';
        // CRITICAL: If updateExisting is true and slot is modules.edit, ALWAYS update (even if content doesn't match patterns)
        const isRoleModulesUpdate = !isConfirmationSummary && !isFeaturesReviewResponse && (
                                   (options?.updateExisting === true && responseSlot === 'modules.edit') ||
                                   (options?.updateExisting === true &&
                                    (aiReply.includes('🚀 **Project Modules by Role**') || aiReply.includes('### 👤 **'))) ||
                                   (responseSlot === 'modules.edit' &&
                                    (aiReply.includes('🚀 **Project Modules by Role**') || aiReply.includes('### 👤 **'))) ||
                                   aiReply.includes('🚀 **Project Modules by Role**') ||
                                   aiReply.includes('### 👤 **')
                                 );
        
        // Helper function to create assistant message object
        const createAssistantMessage = () => ({
          id: `assistant-${Date.now()}-${Math.random().toString(16).slice(2)}`,
          role: "assistant",
          content: aiReply,
          options: responseOptions || null,
          preselected_options: responsePreselectedOptions || null,
          slot: responseSlot || null,
          selection_type: responseSelectionType || null,
          project_scope: responseProjectScope || null,
          ...estimationFieldsFromResponse({
            estimationValue: responseEstimationValue,
            featureOptions: responseFeatureOptions,
            estimationOptions: responseEstimationOptions,
            qaHours: responseQaHours,
            moduleEstimatedHours: responseModuleEstimatedHours,
            estimationSummary: responseEstimationSummary,
            totalHoursDict: responseTotalHoursDict,
            phases: responsePhases,
          }),
          createdAt: new Date(),
        });

        if (isRolesUpdate || isRoleModulesUpdate || isRolePlatformModulesResponse) {
          setMessages(prev => {
            const updated = [...prev];
            // Multi-platform response after Continue: replace the assignment bubble (last assistant message)
            // so the user sees the modules list at the bottom instead of the assignment screen
            if (isRolePlatformModulesResponse && options?.updateExisting === true) {
              for (let i = updated.length - 1; i >= 0; i--) {
                if (updated[i].role === 'assistant') {
                  const isAssignmentBubble = updated[i].slot === 'role_platform_assignments' ||
                    updated[i].content.includes('**Select Platform for your roles**');
                  if (isAssignmentBubble) {
                    updated[i] = {
                      ...updated[i],
                      content: aiReply,
                      options: responseOptions || null,
                      slot: responseSlot || null,
                      selection_type: responseSelectionType || null,
                      ...estimationFieldsFromResponse({
                        estimationValue: responseEstimationValue ?? updated[i].estimation_value,
                        featureOptions: responseFeatureOptions ?? updated[i].feature_options,
                        estimationOptions:
                          responseEstimationOptions ?? updated[i].estimation_options,
                        qaHours: responseQaHours ?? updated[i].qa_hours,
                        moduleEstimatedHours:
                          responseModuleEstimatedHours ?? updated[i].module_estimated_hours,
                        estimationSummary:
                          responseEstimationSummary ?? updated[i].estimation_summary,
                        totalHoursDict: responseTotalHoursDict ?? updated[i].total_hours_dict,
                        phases: responsePhases ?? updated[i].phases,
                      }),
                    };
                    return updated;
                  }
                  break;
                }
              }
            }
            // Find the last roles/role modules message (going backwards)
            for (let i = updated.length - 1; i >= 0; i--) {
              const msg = updated[i];
              if (msg.role === 'assistant') {
                const isRolesMsg = msg.content.includes('👥 **Project Roles**') || 
                                  msg.slot === 'roles.items';
                const isRoleModulesMsg = msg.slot !== 'features.review' && (
                                        msg.content.includes('🚀 **Project Modules by Role**') ||
                                        msg.content.includes('### 👤 **') ||
                                        msg.slot === 'modules.edit'
                                        );
                const isRolePlatformModulesMsg = msg.content.includes('Key Modules for') ||
                                                 msg.content.includes('Role Platform Assignments');
                
                if ((isRolesUpdate && isRolesMsg) || (isRoleModulesUpdate && isRoleModulesMsg) || (isRolePlatformModulesResponse && (isRolePlatformModulesMsg || isRoleModulesMsg))) {
                  updated[i] = {
                    ...msg,
                    content: aiReply,
                    options: responseOptions || null,
                    slot: responseSlot || null,
                    selection_type: responseSelectionType || null,
                    ...estimationFieldsFromResponse({
                      estimationValue: responseEstimationValue ?? msg.estimation_value,
                      featureOptions: responseFeatureOptions ?? msg.feature_options,
                      estimationOptions: responseEstimationOptions ?? msg.estimation_options,
                      qaHours: responseQaHours ?? msg.qa_hours,
                      moduleEstimatedHours:
                        responseModuleEstimatedHours ?? msg.module_estimated_hours,
                      estimationSummary: responseEstimationSummary ?? msg.estimation_summary,
                      totalHoursDict: responseTotalHoursDict ?? msg.total_hours_dict,
                      phases: responsePhases ?? msg.phases,
                    }),
                  };
                  return updated;
                }
              }
            }
            if (options?.updateExisting === true && updated.length > 0) {
              for (let i = updated.length - 1; i >= 0; i--) {
                if (updated[i].role === 'assistant') {
                  updated[i] = {
                    ...updated[i],
                    content: aiReply,
                    options: responseOptions || null,
                    slot: responseSlot || null,
                    selection_type: responseSelectionType || null,
                    ...estimationFieldsFromResponse({
                      estimationValue: responseEstimationValue ?? updated[i].estimation_value,
                      featureOptions: responseFeatureOptions ?? updated[i].feature_options,
                      estimationOptions:
                        responseEstimationOptions ?? updated[i].estimation_options,
                      qaHours: responseQaHours ?? updated[i].qa_hours,
                      moduleEstimatedHours:
                        responseModuleEstimatedHours ?? updated[i].module_estimated_hours,
                      estimationSummary:
                        responseEstimationSummary ?? updated[i].estimation_summary,
                      totalHoursDict: responseTotalHoursDict ?? updated[i].total_hours_dict,
                      phases: responsePhases ?? updated[i].phases,
                    }),
                  };
                  return updated;
                }
              }
            }
            return [...prev, createAssistantMessage()];
          });
        } else {
          setMessages(prev => [...prev, createAssistantMessage()]);
        }
        return {
          aiReply,
          slot: responseSlot,
          options: responseOptions,
          projectScope: responseProjectScope,
        };
      } catch (err) {
        if (options?.uploadCancelRef?.cancelled) {
          clearGenerationRef.current += 1;
          return undefined;
        }
        if (err?.name === "AbortError" && options?.abortSignal) {
          clearGenerationRef.current += 1;
          return undefined;
        }
        console.error(err);
        return undefined;
      } finally {
        setIsLoading(false);
      }
    },
    [sessionId, isLoading, messages, slot]
  );

  // ---------------------------
  // Clear Chat: backend deletes the old Redis session and returns new_session_id; keep that id for all /chat calls.
  // ---------------------------
  const clearChat = useCallback(async () => {
    clearGenerationRef.current += 1;

    let currentSessionId = sessionId;
    try {
      const fromStorage = window.localStorage.getItem(STORAGE_KEYS.SESSION_ID);
      if (fromStorage && fromStorage.trim()) currentSessionId = fromStorage.trim();
    } catch {
      /* ignore */
    }

    let newSessionId = uuidv4();
    try {
      if (currentSessionId) {
        const res = await clearChatSession(currentSessionId);
        const fromApi =
          (typeof res?.new_session_id === "string" && res.new_session_id.trim()) ||
          (typeof res?.newSessionId === "string" && res.newSessionId.trim());
        if (fromApi) {
          newSessionId = String(fromApi).trim();
        }
      }
    } catch (e) {
      console.error("Clear chat (backend) failed; doing local hard reset with new session id", e);
    }

    setMessages([]);
    setIsLoading(false);
    setSlot(null);
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        window.localStorage.setItem(STORAGE_KEYS.SESSION_ID, newSessionId);
      }
    } catch {
      /* ignore */
    }
    setSessionId(newSessionId);
  }, [sessionId]);

  const restoreChat = useCallback((snapshot) => {
    if (!snapshot || typeof snapshot !== "object") return;

    clearGenerationRef.current += 1;

    const nextMessages = Array.isArray(snapshot.messages)
      ? snapshot.messages.map((message, index) => ({
          ...message,
          id: message?.id || `restored-${index}-${Date.now()}`,
        }))
      : [];

    const nextSessionId =
      typeof snapshot.sessionId === "string" && snapshot.sessionId.trim()
        ? snapshot.sessionId.trim()
        : uuidv4();

    const nextSlot =
      typeof snapshot.slot === "string" && snapshot.slot.trim()
        ? snapshot.slot.trim()
        : (
            [...nextMessages]
              .reverse()
              .find((message) => message?.role === "assistant" && typeof message?.slot === "string" && message.slot.trim())
              ?.slot || null
          );

    setMessages(nextMessages);
    setIsLoading(false);
    setSlot(nextSlot);

    try {
      if (typeof window !== "undefined" && window.localStorage) {
        window.localStorage.setItem(STORAGE_KEYS.SESSION_ID, nextSessionId);
      }
    } catch {
      /* ignore */
    }

    setSessionId(nextSessionId);
  }, []);

  return {
    sessionId,
    messages,
    isLoading,
    slot,
    sendMessage,
    clearChat,
    restoreChat,
    injectSystemMessage,
  };
}
